import { AnnotationStageDetailsDialog } from "@/features/annotation/ui/annotation-stage-statuses-dialog";
import {
  getStageStatusColor,
  STAGE_STATUS_TEXT,
} from "@/features/assignment/lib";
import {
  DashboardOrderDetailsResponse,
  useLazyDashboardZoneBasedPredictionsQuery,
  useLazyDashboardPinBasedPredictionsQuery,
  OrderLineUniqueIdentifiers,
  useDashboardSystemCurrentTimeQuery,
} from "@/shared/api";
import { CopyIcon } from "@radix-ui/react-icons";
import {
  Card,
  Heading,
  Inset,
  Flex,
  DataList,
  Badge,
  Box,
  ScrollArea,
  Text,
  Button,
  Skeleton,
  SegmentedControl,
} from "@radix-ui/themes";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useCopyToClipboard } from "react-use";
import { ColumnOverlay } from "./column-overlay";
import { PinBasedPredictionProgress } from "./pin-based-prediction-progress";
import { ZoneBasedPredictionProgress } from "./zone-based-prediction-progress";
import { Row } from "./row";
import {
  CartSegment,
  CartStatus,
  DashboardOrderDetailsGridData,
} from "../lib/types";

export function OrderDetails({
  isLoading,
  data,
  orderId,
}: {
  isLoading: boolean;
  data: DashboardOrderDetailsResponse | undefined;
  orderId: string;
  status: QueryStatus;
}) {
  const [fetchZoneBasedPredictions, zoneBasedPredictionsQueryResult] =
    useLazyDashboardZoneBasedPredictionsQuery();
  const [fetchPinBasedPredictions, pinBasedPredictionsQueryResult] =
    useLazyDashboardPinBasedPredictionsQuery();
  const [isAnomalyReportDialogOpen, setIsAnomalyReportDialogOpen] =
    useState(false);
  const [selectedSegment, setSelectedSegment] = useState<CartSegment>(
    CartSegment.Annotated
  );

  const systemCurrentTimeState = useDashboardSystemCurrentTimeQuery();

  const [, copyToClipboard] = useCopyToClipboard();

  const onCopy = (text: number | string | null | undefined) => {
    if (!text) return;

    copyToClipboard(String(text));
    toast.success("Copied to clipboard");
  };

  const onCopyZoneBasedPredictions = async () => {
    const result = await fetchZoneBasedPredictions({ orderId }).unwrap();

    onCopy(JSON.stringify(result));
  };

  const onCopyPinBasedPredictions = async () => {
    const result = await fetchPinBasedPredictions({ orderId }).unwrap();

    onCopy(JSON.stringify(result));
  };

  const predicate = (uniqueIdentifier: string) => {
    return (line: OrderLineUniqueIdentifiers) =>
      line.sku_base_id?.toString() === uniqueIdentifier ||
      line.barcode === uniqueIdentifier;
  };

  const gridData: DashboardOrderDetailsGridData = useMemo(() => {
    if (!data)
      return {
        annotatedCartStatus: CartStatus.Pending,
        orderLinesStatus: CartStatus.Pending,
        zoneBasedPredictionStatus: CartStatus.Pending,
        pinBasedPredictionStatus: CartStatus.Pending,
        uniqueLines: [],
      };

    const uniqueLines = new Set<string>();

    const addLine = (line: OrderLineUniqueIdentifiers) => {
      uniqueLines.add(line.sku_base_id?.toString() ?? line.barcode);
    };

    const mapLineToRow = (uniqueIdentifier: string) => {
      const orderLine = data.result.order.lines.find(
        predicate(uniqueIdentifier)
      );

      const annotatedLine = data.result.annotated_cart?.find(
        predicate(uniqueIdentifier)
      );

      const zoneBasedPredictionLine =
        data.result.zone_based_prediction_cart?.find(
          predicate(uniqueIdentifier)
        );

      const pinBasedPredictionLine =
        data.result.pin_based_prediction_cart?.find(
          predicate(uniqueIdentifier)
        );

      return {
        updatedAt: undefined,
        orderLine,
        annotatedLine,
        zoneBasedPredictionLine,
        pinBasedPredictionLine,
      };
    };

    if (selectedSegment === CartSegment.Annotated) {
      data.result.annotated_cart?.forEach(addLine);
    } else {
      data.result.order.lines.forEach(addLine);
    }

    data.result.zone_based_prediction_cart?.forEach(addLine);
    data.result.pin_based_prediction_cart?.forEach(addLine);

    return {
      orderLinesStatus:
        data.result.order.lines === null
          ? CartStatus.Null
          : data.result.order.lines.length === 0
            ? CartStatus.Empty
            : CartStatus.Ready,

      annotatedCartStatus:
        data.result.annotated_cart === null
          ? CartStatus.Null
          : data.result.annotated_cart.length === 0
            ? CartStatus.Empty
            : CartStatus.Ready,

      zoneBasedPredictionStatus:
        data.result.zone_based_prediction_cart === null
          ? CartStatus.Null
          : data.result.zone_based_prediction_cart.length === 0
            ? CartStatus.Empty
            : CartStatus.Ready,
      pinBasedPredictionStatus:
        data.result.pin_based_prediction_cart === null
          ? CartStatus.Null
          : data.result.pin_based_prediction_cart.length === 0
            ? CartStatus.Empty
            : CartStatus.Ready,
      uniqueLines: Array.from(uniqueLines.values())
        .map(mapLineToRow)
        .sort((a, b) => {
          if (selectedSegment === CartSegment.Annotated) {
            if (a.annotatedLine && b.annotatedLine) {
              return a.annotatedLine.name.localeCompare(b.annotatedLine.name);
            }
          } else {
            if (a.orderLine && b.orderLine) {
              return a.orderLine.name.localeCompare(b.orderLine.name);
            }
          }

          return 0;
        }),
    };
  }, [data, selectedSegment]);

  const { start, end } = useMemo(() => {
    if (
      !data?.result.journey_snapshots ||
      data.result.journey_snapshots.length === 0
    )
      return { start: null, end: null };

    const allEnterTimes = data.result.journey_snapshots.map((journey) =>
      new Date(journey.enter_time).getTime()
    );
    const allExitTimes = data.result.journey_snapshots.map((journey) =>
      new Date(journey.exit_time).getTime()
    );

    const start = Math.min(...allEnterTimes);
    const end = Math.max(...allExitTimes);

    return {
      start: start ? new Date(start).toISOString() : null,
      end: end ? new Date(end).toISOString() : null,
    };
  }, [data]);

  return (
    <Card>
      <Heading>Dashboard Order Details</Heading>
      <Inset clip="padding-box" side="all">
        <Flex className="bg-[var(--gray-3)]" my="5" px="3" py="1" gap="5">
          <Flex gap="2">
            <Text size="2" color="gray">
              Order ID:
            </Text>
            <Skeleton className="min-w-56" loading={isLoading}>
              <Button
                variant="ghost"
                highContrast
                color="gray"
                onClick={() => onCopy(data?.result.order.id)}
              >
                {isLoading ? "Loading..." : data?.result.order.id || "-"}
              </Button>
            </Skeleton>
          </Flex>
          <Flex gap="2">
            <Text size="2" color="gray">
              Order source:
            </Text>
            <Skeleton className="min-w-56" loading={isLoading}>
              <Button
                variant="ghost"
                highContrast
                color="gray"
                onClick={() => onCopy(data?.result.order.order_source)}
              >
                {isLoading
                  ? "Loading..."
                  : data?.result.order.order_source || "-"}
              </Button>
            </Skeleton>
          </Flex>
          <Flex gap="2">
            <Text size="2" color="gray">
              Request ID:
            </Text>
            <Skeleton className="min-w-56" loading={isLoading}>
              <Button
                variant="ghost"
                highContrast
                color="gray"
                onClick={() => onCopy(data?.result.request_id)}
              >
                {isLoading ? "Loading..." : data?.result.request_id || "-"}
              </Button>
            </Skeleton>
          </Flex>
          <Flex gap="2">
            <Text size="2" color="gray">
              Created at:
            </Text>
            <Skeleton className="min-w-56" loading={isLoading}>
              <Button
                variant="ghost"
                highContrast
                color="gray"
                onClick={() => onCopy(data?.result.order.created_at)}
              >
                {isLoading
                  ? "Loading..."
                  : data?.result.order.created_at || "-"}
              </Button>
            </Skeleton>
          </Flex>

          <Flex gap="2">
            <Text size="2" color="gray">
              Order time:
            </Text>
            <Skeleton className="min-w-56" loading={isLoading}>
              <Button
                variant="ghost"
                highContrast
                color="gray"
                onClick={() => onCopy(data?.result.order.time)}
              >
                {isLoading ? "Loading..." : data?.result.order.time || "-"}
              </Button>
            </Skeleton>
          </Flex>
        </Flex>
      </Inset>

      <Inset clip="padding-box" side="all">
        <Flex className="border-b border-gray-200">
          <Flex
            direction="column"
            flexBasis="33.333333%"
            className="border-r border-gray-200 pl-3 pb-2"
          >
            <Flex gap="2" mr="2" justify="between" align="start">
              <Flex align="baseline" gap="2">
                <Heading size="3">Cart items</Heading>
                <Text size="1" color="gray">
                  {selectedSegment === CartSegment.Annotated
                    ? `Total ${data?.result?.annotated_cart?.length || 0} items`
                    : `Total ${data?.result?.order?.lines?.length || 0} items`}
                </Text>
              </Flex>
              <SegmentedControl.Root
                value={selectedSegment}
                className="h-7"
                onValueChange={(value) =>
                  setSelectedSegment(value as CartSegment)
                }
                size="2"
              >
                <SegmentedControl.Item
                  className="font-bold !h-7"
                  value={CartSegment.Annotated}
                >
                  Annotated
                </SegmentedControl.Item>
                <SegmentedControl.Item
                  className="font-bold !h-7"
                  value={CartSegment.OrderLines}
                >
                  Order
                </SegmentedControl.Item>
              </SegmentedControl.Root>
            </Flex>

            <DataList.Root size="1" className="gap-y-2 my-2">
              <DataList.Item>
                <DataList.Label>Start time:</DataList.Label>
                <DataList.Value>
                  <Button
                    variant="ghost"
                    color="gray"
                    size="1"
                    highContrast
                    onClick={() => onCopy(start)}
                  >
                    {start || "-"}
                  </Button>
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>End time:</DataList.Label>
                <DataList.Value>
                  <Button
                    variant="ghost"
                    color="gray"
                    size="1"
                    highContrast
                    onClick={() => onCopy(end)}
                  >
                    {end || "-"}
                  </Button>
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Journey count:</DataList.Label>
                <DataList.Value>
                  <Button
                    variant="ghost"
                    color="gray"
                    size="1"
                    highContrast
                    onClick={() =>
                      onCopy(data?.result.journey_snapshots?.length || "")
                    }
                  >
                    {!data
                      ? "-"
                      : data.result.journey_snapshots === null
                        ? 0
                        : data.result.journey_snapshots?.length}
                  </Button>
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Journey snapshots created at:</DataList.Label>
                <DataList.Value>
                  <Button
                    variant="ghost"
                    color="gray"
                    size="1"
                    highContrast
                    onClick={() =>
                      onCopy(data?.result.journey_snapshots_created_at)
                    }
                  >
                    {data?.result.journey_snapshots_created_at || "-"}
                  </Button>
                </DataList.Value>
              </DataList.Item>
              {data?.result.order.stage_detail_statuses.map((stage) => (
                <DataList.Item key={stage.stage}>
                  <DataList.Label className="capitalize">
                    {stage.stage.split("_").join(" ")}:
                  </DataList.Label>
                  <DataList.Value>
                    <Badge
                      color={getStageStatusColor(stage.status)}
                      variant="soft"
                    >
                      {STAGE_STATUS_TEXT[stage.status]}
                    </Badge>
                  </DataList.Value>
                </DataList.Item>
              ))}
              <DataList.Item>
                <DataList.Label className="capitalize">
                  Anomaly report:
                </DataList.Label>
                <DataList.Value>
                  {data?.result.order?.anomaly_report ? (
                    <Button
                      variant="ghost"
                      color="gray"
                      size="1"
                      className="w-[calc(100%-1rem)] justify-start"
                      onClick={() => setIsAnomalyReportDialogOpen(true)}
                      highContrast
                    >
                      <Text className="truncate">
                        {data?.result.order.anomaly_report}
                      </Text>
                    </Button>
                  ) : (
                    <Text color="gray" highContrast>
                      -
                    </Text>
                  )}
                </DataList.Value>
              </DataList.Item>
            </DataList.Root>
            <Box mb="2">
              <AnnotationStageDetailsDialog
                open={isAnomalyReportDialogOpen}
                onOpenChange={setIsAnomalyReportDialogOpen}
                order={data?.result.order || null}
                trigger={
                  <Button variant="soft" color="gray" size="1">
                    Show Annotation Stage Details
                  </Button>
                }
              />
            </Box>
          </Flex>
          <Box
            flexBasis="33.333333%"
            className="border-r border-gray-200 pl-2 pb-2"
          >
            <Flex gap="2" align="baseline" mb="2">
              <Heading size="3">Zone based prediction cart</Heading>

              <Text size="1" color="gray">
                Total {data?.result?.zone_based_prediction_cart?.length || 0}{" "}
                items
              </Text>
            </Flex>
            <ZoneBasedPredictionProgress
              orderDetails={data?.result}
              data={systemCurrentTimeState.data}
              isLoading={systemCurrentTimeState.isLoading || isLoading}
            />
            <Heading size="1" mb="0" mt="1">
              Prediction Evaluation
            </Heading>
            <DataList.Root size="1" className="gap-y-0">
              <DataList.Item>
                <DataList.Label>F1 score:</DataList.Label>
                <DataList.Value>
                  {data?.result.zone_based_prediction_evaluation?.f1_score ??
                    "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>True positives count:</DataList.Label>
                <DataList.Value>
                  {data?.result.zone_based_prediction_evaluation
                    ?.true_positives_count ?? "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>False positive count:</DataList.Label>
                <DataList.Value>
                  {data?.result.zone_based_prediction_evaluation
                    ?.false_positives_count ?? "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>False negative count:</DataList.Label>
                <DataList.Value>
                  {data?.result.zone_based_prediction_evaluation
                    ?.false_negatives_count ?? "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Evaluation created at:</DataList.Label>
                <DataList.Value>
                  {data?.result.zone_based_prediction_evaluation?.created_at ??
                    "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Prediction created time:</DataList.Label>
                <DataList.Value>
                  {data?.result.zone_based_prediction_created_at ?? "-"}
                </DataList.Value>
              </DataList.Item>
            </DataList.Root>
            <Button
              variant="soft"
              color="gray"
              size="1"
              mt="4"
              loading={zoneBasedPredictionsQueryResult.isFetching}
              onClick={onCopyZoneBasedPredictions}
            >
              <CopyIcon />
              Copy Zone-based Transaction Predictions
            </Button>
          </Box>
          <Box flexBasis="33.3333333%" className="pl-2 pb-2">
            <Flex gap="2" align="baseline" mb="2">
              <Heading size="3">Pin based prediction cart</Heading>

              <Text size="1" color="gray">
                Total {data?.result?.pin_based_prediction_cart?.length || 0}{" "}
                items
              </Text>
            </Flex>
            <PinBasedPredictionProgress
              data={systemCurrentTimeState.data}
              orderDetails={data?.result}
              isLoading={systemCurrentTimeState.isLoading || isLoading}
              journeyCount={data?.result.journey_snapshots?.length}
            />
            <Heading size="1" mb="0" mt="1">
              Prediction Evaluation
            </Heading>
            <DataList.Root size="1" className="gap-y-0">
              <DataList.Item>
                <DataList.Label>F1 score:</DataList.Label>
                <DataList.Value>
                  {data?.result.pin_based_prediction_evaluation?.f1_score ??
                    "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>True positives count:</DataList.Label>
                <DataList.Value>
                  {data?.result.pin_based_prediction_evaluation
                    ?.true_positives_count ?? "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>False positive count:</DataList.Label>
                <DataList.Value>
                  {data?.result.pin_based_prediction_evaluation
                    ?.false_positives_count ?? "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>False negative count:</DataList.Label>
                <DataList.Value>
                  {data?.result.pin_based_prediction_evaluation
                    ?.false_negatives_count ?? "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Evaluation created at:</DataList.Label>
                <DataList.Value>
                  {data?.result.pin_based_prediction_evaluation?.created_at ??
                    "-"}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Prediction created time:</DataList.Label>
                <DataList.Value>
                  {data?.result.pin_based_prediction_created_at ?? "-"}
                </DataList.Value>
              </DataList.Item>
            </DataList.Root>

            <Button
              variant="soft"
              color="gray"
              size="1"
              mt="4"
              loading={pinBasedPredictionsQueryResult.isFetching}
              onClick={onCopyPinBasedPredictions}
            >
              <CopyIcon />
              Copy Pin-based Transaction Predictions
            </Button>
          </Box>
        </Flex>
        <ScrollArea className="h-[calc(100dvh-554px)]">
          <div className="w-full">
            <ColumnOverlay
              selectedSegment={selectedSegment}
              annotatedCartStatus={gridData.annotatedCartStatus}
              orderLinesStatus={gridData.orderLinesStatus}
              zoneBasedPredictionStatus={gridData.zoneBasedPredictionStatus}
              pinBasedPredictionStatus={gridData.pinBasedPredictionStatus}
              isLoading={isLoading}
              journeyCount={data?.result.journey_snapshots?.length}
            />
            {gridData.uniqueLines.map((row, index) => (
              <Row selectedSegment={selectedSegment} key={index} row={row} />
            ))}
          </div>
        </ScrollArea>
      </Inset>
    </Card>
  );
}
